/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'
import moment from 'moment'

import {
  Table,
  Spinner,
  PageNavigationButtons,
  CenteredContainerDisplay,
  SvgIcon,
  NavLink,
  SpanWithData,
  Warning
} from '../'

import {
  PageNavigationControl,
  decodeBase64,
  saveCursor,
  getPageCursor
} from '../../utils'

class WorkflowRuns extends React.Component {
  static propTypes = {
    workflowRuns: PropTypes.object,
    status: PropTypes.string,
    workflow: PropTypes.string,
    scheduledAtStartDate: PropTypes.string,
    scheduledAtEndDate: PropTypes.string,
    processedAtStartDate: PropTypes.string,
    processedAtEndDate: PropTypes.string,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func
  }

  static defaultProps = {
    resultsPerPage: 15
  }

  constructor(props) {
    super(props)

    this.state = {
      pageNav: new PageNavigationControl(),
      filterChanged: false
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ((this.props.status !== nextProps.status ||
      this.props.workflow !== nextProps.workflow ||
      this.props.scheduledAtStartDate !== nextProps.scheduledAtStartDate ||
      this.props.scheduledAtEndDate !== nextProps.scheduledAtEndDate ||
      this.props.processedAtStartDate !== nextProps.processedAtStartDate ||
      this.props.processedAtEndDate !== nextProps.processedAtEndDate ||
      this.props.orderBy !== nextProps.orderBy) &&
      !sessionStorage.getItem('workflowRunListPageState')
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem('workflowRunListFilters'))
      if (previousFilters && (
        previousFilters.status === nextProps.status ||
        previousFilters.workflow === nextProps.workflow ||
        previousFilters.scheduledAtStartDate === nextProps.scheduledAtStartDate ||
        previousFilters.scheduledAtEndDate === nextProps.scheduledAtEndDate ||
        previousFilters.processedAtStartDate === nextProps.processedAtStartDate ||
        previousFilters.processedAtEndDate === nextProps.processedAtEndDate ||
        previousFilters.orderBy === nextProps.orderBy
      )) {
        sessionStorage.removeItem('workflowRunListFilters')
      } else {
        this.state.pageNav.reset()
        this.setState({
          filterChanged: true
        }, () => this.props.refetch())
        sessionStorage.removeItem('workflowRunListFilters')
      }
    } else {
      if (!nextProps.loading) {
        if (this.state.filterChanged) {
          this.setState({
            filterChanged: false
          })
          this.state.pageNav.setPageStates(null, nextProps.workflowruns)
        }
        else if (nextProps.workflowruns) {
          let workflowRunListPageState = JSON.parse(sessionStorage.getItem('workflowRunListPageState'))
          if (workflowRunListPageState) {
            this.state.pageNav.setPageStates(null, null, workflowRunListPageState)
            sessionStorage.removeItem('workflowRunListPageState')
            this.props.refetch({ cursor: workflowRunListPageState.cursor })
          } else if (!Object.is(this.props.workflowruns, nextProps.workflowruns)) {
            this.state.pageNav.setPageStates(this.props.workflowruns, nextProps.workflowruns)
          }
        }
      }
    }
  }

  savePaginationAndFilterState = (startCursor) => {
    sessionStorage.setItem('workflowRunListPageState', JSON.stringify({
      firstPage: this.state.pageNav.firstPage,
      lastPage: this.state.pageNav.lastPage,
      currentPage: this.state.pageNav.currentPage,
      pages: this.state.pageNav.pages,
      cursor: saveCursor(startCursor)
    }))
    sessionStorage.setItem('workflowRunListFilters', JSON.stringify({
      status: this.props.status,
      workflow: this.props.workflow,
      scheduledAtStartDate: this.props.scheduledAtStartDate,
      scheduledAtEndDate: this.props.scheduledAtEndDate,
      processedAtStartDate: this.props.processedAtStartDate,
      processedAtEndDate: this.props.processedAtEndDate,
      orderBy: this.props.orderBy
    }))
  }

  saveStateAndGoToDetailPage = (workflowRunId, startCursor) => {
    this.savePaginationAndFilterState(startCursor)
    this.props.history.push(`/os/operations/workflows/${workflowRunId}`)
  }

  render() {
    if (this.props.loading) {
      return (
        <CenteredContainerDisplay
          content={
            <Spinner className='slds-is-relative' />  // TODO: loading state stencil instead
          }
          yAdjustPercentage={-15}
        />
      )
    }

    if (this.props.error) {
      return (
        <Warning className='slds-m-around_large'>
          Failed to load workflow runs.
        </Warning>
      )
    }

    if (this.props.workflowruns.edges.length === 0) {
      return (
        <div className='slds-m-around_large'>
          <SvgIcon
            variant='utility'
            symbol='info'
            colour='light'
            size='small'
            className='slds-m-right_small'
          />
          {`No ${this.props.status} workflow run records to display`}
        </div>
      )
    }

    let pageNav = this.state.pageNav
    let tableColumns = ['Description', 'Status', 'Created', 'Scheduled']

    let workflowRuns = this.props.workflowruns
    let startCursor = workflowRuns.pageInfo.startCursor
    let totalCount = workflowRuns.totalCount

    return (
      <div className='slds-scrollable_x'>
        <Table
          columns={tableColumns}
          rows={
            workflowRuns.edges.map((workflowRun, index) => (
              <WorkflowRunDetailsRow
                workflowRun={workflowRun.node}
                onClick={workflowRunId => this.saveStateAndGoToDetailPage(workflowRunId, startCursor)}
                key={`workflowRunDetailsRow_${index}`}
              />
            ))
          }
          objectRows={true}
          orderBy={this.props.orderBy}
          changeSort={this.props.changeSort}
          querySortableColumns={{
            'Created': {
              orderByName: 'created_at'
            },
            'Scheduled': {
              orderByName: 'scheduled_at'
            }
          }}
        />
        <PageNavigationButtons
          onNextClick={() => pageNav.nextPage(this.props.loadNext)}
          onPreviousClick={() => pageNav.previousPage(this.props.loadPrevious)}
          previousDisabled={pageNav.firstPage}
          nextDisabled={pageNav.lastPage}
          startCursor={startCursor}
          totalCount={totalCount}
          resultsPerPage={this.props.resultsPerPage}
          resultsText='workflow runs'
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
          className='slds-m-top_small slds-clearfix slds-float_right'
        />
      </div>
    )
  }
}

const WorkflowRunDetailsRow = ({ workflowRun }) => (
  <tr className='slds-hint-parent'>
    <th scope='row'>
      <div className='slds-truncate' title={workflowRun.description}>
        <NavLink to={'/os/operations/general/workflows/' + workflowRun.id} >
          {workflowRun.workflow.name + ' | ' + workflowRun.description}
        </NavLink>
      </div>
    </th>
    <td role='gridcell'>
      <div className='slds-truncate' title={workflowRun.status}>
        <SpanWithData forField='WorkflowRunStatus' searchKey={workflowRun.status} />
      </div>
    </td>
    <td role='gridcell'>
      <div className='slds-truncate' title={workflowRun.createdAt}>
        {moment(workflowRun.createdAt).format('llll')}
      </div>
    </td>
    <td role='gridcell'>
      <div className='slds-truncate' title={workflowRun.scheduledAt}>
        {moment(workflowRun.scheduledAt).format('llll')}
      </div>
    </td>
  </tr>
)

export const getWorkflowRuns = gql`
  query($search: String, $status: String!, $workflow: UUID, $scheduledAtStartDate: DateTime, $scheduledAtEndDate: DateTime, $processedAtStartDate: DateTime, $processedAtEndDate: DateTime, $resultsPerPage: Int!, $orderBy: String, $cursor: String) {
    workflowruns(description_Icontains: $search, status: $status, workflow_Id: $workflow, scheduledOnOrAfter: $scheduledAtStartDate, scheduledOnOrBefore: $scheduledAtEndDate, processedOnOrAfter: $processedAtStartDate, processedOnOrBefore: $processedAtEndDate, orderBy: $orderBy, first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          ...WorkflowRun
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.workflowRun}
`

const getPreviousWorkflowRuns = gql`
  query($search: String, $status: String!, $workflow: UUID, $scheduledAtStartDate: DateTime, $scheduledAtEndDate: DateTime, $processedAtStartDate: DateTime, $processedAtEndDate: DateTime, $resultsPerPage: Int!, $orderBy: String, $cursor: String) {
    workflowruns(description_Icontains: $search, status: $status, workflow_Id: $workflow, scheduledOnOrAfter: $scheduledAtStartDate, scheduledOnOrBefore: $scheduledAtEndDate, processedOnOrAfter: $processedAtStartDate, processedOnOrBefore: $processedAtEndDate, orderBy: $orderBy, last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          ...WorkflowRun
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.workflowRun}
`

const withData = graphql(getWorkflowRuns, {
    options: ({ search, status, workflow, scheduledAtStartDate, scheduledAtEndDate, processedAtStartDate, processedAtEndDate, orderBy, resultsPerPage = 15, cursor }) => ({
      variables: {
        search,
        status,
        workflow: workflow ? decodeBase64(workflow) : undefined,
        scheduledAtStartDate,
        scheduledAtEndDate,
        processedAtStartDate,
        processedAtEndDate,
        orderBy,
        resultsPerPage,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { search, status, workflow, scheduledAtStartDate, scheduledAtEndDate, processedAtStartDate, processedAtEndDate, orderBy, resultsPerPage = 15 }, data: { loading, error, workflowruns, fetchMore, refetch } }) => ({
      loading,
      error,
      workflowruns,
      refetch,
      loadNext: () => {
        return fetchMore({
          query: getWorkflowRuns,
          variables: {
            search,
            status,
            workflow: workflow ? decodeBase64(workflow) : undefined,
            scheduledAtStartDate,
            scheduledAtEndDate,
            processedAtStartDate,
            processedAtEndDate,
            orderBy,
            resultsPerPage,
            cursor: workflowruns.pageInfo.endCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.workflowruns.edges
            const pageInfo = fetchMoreResult.workflowruns.pageInfo

            return {
              workflowruns: {
                __typename: previousResult.workflowruns.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      loadPrevious: () => {
        return fetchMore({
          query: getPreviousWorkflowRuns,
          variables: {
            search,
            status,
            workflow: workflow ? decodeBase64(workflow) : undefined,
            scheduledAtStartDate,
            scheduledAtEndDate,
            processedAtStartDate,
            processedAtEndDate,
            orderBy,
            resultsPerPage,
            cursor: workflowruns.pageInfo.startCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.workflowruns.edges
            const pageInfo = fetchMoreResult.workflowruns.pageInfo

            return {
              workflowruns: {
                __typename: previousResult.workflowruns.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withData,
  withRouter
)(WorkflowRuns)



// WEBPACK FOOTER //
// ./src/components/workflow-runs/index.js